/**
 * Aba "Agenda nativa" da Tela A2 (Config Tenant) — Frente B Plano 3 (B1).
 *
 * Expõe dois campos REAIS do `tenant-config` (R15):
 *   - `nativeSchedulingModeDefault` — modo default de exposição da agenda nativa
 *     (Eixo A, raiz do cascade `resolveNativeSchedulingMode` Cliente▸Playbook▸Tenant▸plataforma).
 *   - `waitlistPolicy` — política da fila de espera (shape espelha `normalizeWaitlistPolicy`
 *     do engine; o motor lê exatamente estas chaves — nada inventado no front).
 *
 * Gating: o modo default gateia por `scheduling-native`; a política de fila por
 * `scheduling-waitlist`. Sem as caps → honest-defer (a config existe, mas fica inerte).
 * O FETCH da config já é da página inteira (outras abas) — aqui só se condiciona o RENDER
 * por cap. Escrita gated em `tenant-config-manage`.
 *
 * Admin Hub T10 — os botões locais Descartar/Salvar saíram; a seção registra na
 * `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do `GeneralTab`,
 * T7, e do `OperatorShiftTab`, T9). ⚠️ Grava agora com `update.mutateAsync` (não
 * `update.mutate`) — o `save()` da registration devolve `Promise<void>`, e o
 * `StickySaveBar` precisa aguardar o PATCH terminar antes de decidir sucesso/erro
 * do toast (mesma correção que T7/T9 já aplicaram). Cabeçalho próprio também
 * saiu — dentro de uma `DomainSection` o título/descrição já vêm do catálogo
 * (item.label/item.efeito; a nota de cascade "playbooks e clientes específicos
 * podem sobrepor este padrão" foi dobrada em `catalog.ts` — vide `catalog.spec.ts`).
 *
 * ★ RULING do coordenador (T10 dispatch) — NÃO existe (nem se constrói) uma
 * dependência "lembretes desabilitados quando agenda nativa está off". Esta
 * aba serve tenants SEM ERP de agendamento; tenants COM ERP agendam por lá e
 * continuam precisando de lembretes (`RemindersTab` é agnóstico à origem do
 * agendamento — próprio docblock dele). Ver `AgendaLembretesPage.spec.tsx`
 * para o teste de INDEPENDÊNCIA que assere isso.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useSectionSave } from '../hub/save-bar-context';

import { SCHEDULING_TAB_FIELD_LABELS } from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type {
  NativeSchedulingMode,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
  WaitlistPolicyWire,
  WaitlistTier,
} from './types';

/** Defaults de plataforma p/ a política de fila (espelham `normalizeWaitlistPolicy`). */
const PLATFORM_POLICY: WaitlistPolicyWire = {
  promotionMode: 'sequential',
  offerWindowMinutes: 30,
  selfService: 'open',
  maxQueueSize: null,
  noShowPenalty: null,
};

const MODE_OPTIONS: readonly { value: NativeSchedulingMode; title: string; desc: string }[] = [
  {
    value: 'propose_and_book',
    title: 'A IA agenda sozinha',
    desc: 'Fecha o horário direto na conversa com o cliente verificado. Ideal para o profissional solo que quer a IA resolvendo o agendamento.',
  },
  {
    value: 'propose_only',
    title: 'A IA propõe; um humano confirma',
    desc: 'A IA sugere horários livres, mas o agendamento final passa por um atendente. Conservador — bom para começar.',
  },
  {
    value: 'qualify_only',
    title: 'A IA só qualifica',
    desc: 'A IA levanta o interesse e encaminha; não sugere nem agenda horários.',
  },
];

const PROMOTION_OPTIONS: readonly { value: WaitlistPolicyWire['promotionMode']; label: string }[] =
  [
    { value: 'sequential', label: 'Um a um, por ordem da fila' },
    { value: 'broadcast', label: 'Oferta a todos ao mesmo tempo' },
    { value: 'manual', label: 'Liberação manual pelo operador' },
  ];

const TIER_LABELS: Record<WaitlistTier, string> = {
  bumped: 'Remanejado pela casa',
  urgent_high: 'Urgência alta',
  urgent_medium: 'Urgência média',
  urgent_low: 'Urgência baixa',
  preferred: 'Prioritário',
  normal: 'Normal',
};

interface FormState {
  mode: NativeSchedulingMode;
  policy: WaitlistPolicyWire;
  penaltyOn: boolean;
}

function toForm(c: SerializedTenantConfig): FormState {
  const policy = c.waitlistPolicy ?? PLATFORM_POLICY;
  return {
    mode: c.nativeSchedulingModeDefault,
    policy,
    penaltyOn: policy.noShowPenalty !== null,
  };
}

export function SchedulingTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const canNative = useCapability('scheduling-native');
  const canWaitlist = useCapability('scheduling-waitlist');
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  // Payload de escrita a partir de UM form — usado nos dois sentidos:
  // `buildRequest(form)` (o que enviar) e `buildRequest(serverForm)` (valores
  // antigos, para o `undo`, pela MESMA mutation). Fecha sobre `canWaitlist`
  // (constante durante o ciclo de vida do componente): sem a cap, o PATCH
  // nunca inclui `waitlistPolicy` — mesmo comportamento de antes (T10).
  const buildRequest = (f: FormState): UpdateTenantConfigRequest => {
    const policyToSend: WaitlistPolicyWire = {
      ...f.policy,
      noShowPenalty: f.penaltyOn
        ? (f.policy.noShowPenalty ?? {
            enabled: true,
            lookbackDays: 90,
            threshold: 2,
            demote: 'normal',
          })
        : null,
    };
    return {
      nativeSchedulingModeDefault: f.mode,
      ...(canWaitlist && { waitlistPolicy: policyToSend }),
    };
  };

  // Só os 2 campos reais que MUDARAM — `waitlistPolicy` só entra quando a cap
  // de fila está presente (mesmo gate do payload real acima).
  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels: string[] = [];
    if (form.mode !== serverForm.mode) {
      labels.push(SCHEDULING_TAB_FIELD_LABELS.nativeSchedulingModeDefault);
    }
    const policyChanged =
      form.penaltyOn !== serverForm.penaltyOn ||
      JSON.stringify(form.policy) !== JSON.stringify(serverForm.policy);
    if (canWaitlist && policyChanged) {
      labels.push(SCHEDULING_TAB_FIELD_LABELS.waitlistPolicy);
    }
    return labels;
  }, [form, serverForm, canWaitlist]);

  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de honest-defer/loading/erro abaixo.
  useSectionSave(
    isDirty && canManage && canNative && form !== null && serverForm !== null
      ? {
          sectionId: 'agenda-nativa',
          changedLabels,
          save: async () => {
            previousValues.current = buildRequest(serverForm);
            await update.mutateAsync(buildRequest(form));
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (!canNative) {
    return (
      <div className="max-w-2xl">
        <p className="rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          A agenda nativa não está habilitada para este tenant. Ative a capability{' '}
          <code className="font-mono">scheduling-native</code> para configurar o modo de agendamento
          pela IA e a política de fila.
        </p>
      </div>
    );
  }

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;

  const patchPolicy = (patch: Partial<WaitlistPolicyWire>): void => {
    setForm((f) => (f === null ? f : { ...f, policy: { ...f.policy, ...patch } }));
  };
  const patchPenalty = (patch: Partial<NonNullable<WaitlistPolicyWire['noShowPenalty']>>): void => {
    setForm((f) => {
      if (f === null) return f;
      const base = f.policy.noShowPenalty ?? {
        enabled: true,
        lookbackDays: 90,
        threshold: 2,
        demote: 'normal' as WaitlistTier,
      };
      return { ...f, policy: { ...f.policy, noShowPenalty: { ...base, ...patch } } };
    });
  };

  const policy = form.policy;

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (Admin Hub T10) — DomainSection já supre título
          (item.label) + descrição (item.efeito, agora com a nota de cascade
          dobrada — catalog.ts) quando esta aba é composta no hub. */}
      <div className="space-y-6">
        {/* Modo default de exposição (Eixo A) */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <h3 className="text-sm font-medium text-text-primary">Modo de agendamento pela IA</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Padrão do tenant (raiz do cascade). A garantia de horário livre é sempre do sistema — a
            IA nunca agenda sobre um horário ocupado.
          </p>
          <div className="space-y-2">
            {MODE_OPTIONS.map((opt) => {
              const selected = form.mode === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    selected ? 'border-accent bg-accent/5' : 'border-border-subtle hover:bg-app-bg'
                  } ${fieldsDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <input
                    type="radio"
                    name="native-mode"
                    className="mt-0.5 accent-accent"
                    checked={selected}
                    disabled={fieldsDisabled}
                    onChange={() => {
                      setForm((f) => (f === null ? f : { ...f, mode: opt.value }));
                    }}
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary">{opt.title}</span>
                    <span className="mt-0.5 block text-xs text-text-secondary">{opt.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Política da fila de espera */}
        {canWaitlist ? (
          <div className="rounded-xl border border-border-subtle bg-surface p-4">
            <h3 className="text-sm font-medium text-text-primary">Política da fila de espera</h3>
            <p className="mt-1 mb-4 text-xs text-text-secondary">
              Como a fila é oferecida quando um horário abre, e as regras de entrada.
            </p>

            <div className="space-y-4">
              {/* Modo de promoção */}
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-text-primary">
                  Quando um horário abre
                </span>
                <select
                  aria-label="Modo de promoção da fila"
                  disabled={fieldsDisabled}
                  value={policy.promotionMode}
                  onChange={(e) => {
                    patchPolicy({
                      promotionMode: e.target.value as WaitlistPolicyWire['promotionMode'],
                    });
                  }}
                  className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                >
                  {PROMOTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* Janela da oferta */}
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-text-primary">
                  Tempo para o cliente responder à oferta (minutos)
                </span>
                <input
                  type="number"
                  aria-label="Janela da oferta em minutos"
                  min={1}
                  max={1440}
                  disabled={fieldsDisabled}
                  value={policy.offerWindowMinutes}
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value, 10);
                    patchPolicy({ offerWindowMinutes: Number.isNaN(n) ? 1 : n });
                  }}
                  className="w-32 rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                />
              </label>

              {/* Aprovação para entrar */}
              <div className="flex items-start justify-between gap-3">
                <div className="pr-4">
                  <span className="block text-sm font-medium text-text-primary">
                    Exigir aprovação para entrar na fila
                  </span>
                  <span className="mt-0.5 block text-xs text-text-secondary">
                    Ligado: o operador aprova cada entrada. Desligado: o cliente entra direto.
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={policy.selfService === 'requires_approval'}
                  aria-label="Exigir aprovação para entrar na fila"
                  disabled={fieldsDisabled}
                  onClick={() => {
                    patchPolicy({
                      selfService:
                        policy.selfService === 'requires_approval' ? 'open' : 'requires_approval',
                    });
                  }}
                  className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                    policy.selfService === 'requires_approval' ? 'bg-accent' : 'bg-border-subtle'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                      policy.selfService === 'requires_approval'
                        ? 'translate-x-[18px]'
                        : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Limite de fila */}
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-text-primary">
                  Limite de pessoas na fila
                </span>
                <input
                  type="number"
                  aria-label="Limite de pessoas na fila"
                  min={1}
                  max={10000}
                  placeholder="Sem limite"
                  disabled={fieldsDisabled}
                  value={policy.maxQueueSize ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = Number.parseInt(v, 10);
                    patchPolicy({ maxQueueSize: v === '' || Number.isNaN(n) ? null : n });
                  }}
                  className="w-32 rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                />
                <span className="mt-1 block text-xs text-text-secondary">
                  Deixe vazio para não limitar.
                </span>
              </label>

              {/* Penalidade por falta */}
              <div className="rounded-lg border border-border-subtle p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="pr-4">
                    <span className="block text-sm font-medium text-text-primary">
                      Penalizar faltas (no-show)
                    </span>
                    <span className="mt-0.5 block text-xs text-text-secondary">
                      Rebaixa a prioridade na fila de quem faltou repetidamente.
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.penaltyOn}
                    aria-label="Penalizar faltas"
                    disabled={fieldsDisabled}
                    onClick={() => {
                      setForm((f) => (f === null ? f : { ...f, penaltyOn: !f.penaltyOn }));
                    }}
                    className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                      form.penaltyOn ? 'bg-accent' : 'bg-border-subtle'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                        form.penaltyOn ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                {form.penaltyOn && (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-text-secondary">
                        Faltas para penalizar
                      </span>
                      <input
                        type="number"
                        aria-label="Número de faltas para penalizar"
                        min={1}
                        max={100}
                        disabled={fieldsDisabled}
                        value={policy.noShowPenalty?.threshold ?? 2}
                        onChange={(e) => {
                          const n = Number.parseInt(e.target.value, 10);
                          patchPenalty({ threshold: Number.isNaN(n) ? 1 : n });
                        }}
                        className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-text-secondary">
                        Janela (dias)
                      </span>
                      <input
                        type="number"
                        aria-label="Janela de análise em dias"
                        min={1}
                        max={3650}
                        disabled={fieldsDisabled}
                        value={policy.noShowPenalty?.lookbackDays ?? 90}
                        onChange={(e) => {
                          const n = Number.parseInt(e.target.value, 10);
                          patchPenalty({ lookbackDays: Number.isNaN(n) ? 1 : n });
                        }}
                        className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-text-secondary">
                        Rebaixar para
                      </span>
                      <select
                        aria-label="Tier de rebaixamento"
                        disabled={fieldsDisabled}
                        value={policy.noShowPenalty?.demote ?? 'normal'}
                        onChange={(e) => {
                          patchPenalty({ demote: e.target.value as WaitlistTier });
                        }}
                        className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
                      >
                        {(Object.keys(TIER_LABELS) as WaitlistTier[]).map((t) => (
                          <option key={t} value={t}>
                            {TIER_LABELS[t]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
            A fila de espera não está habilitada para este tenant. Ative a capability{' '}
            <code className="font-mono">scheduling-waitlist</code> para configurar a política de
            fila.
          </p>
        )}
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T10); sucesso/erro do save viram toast da barra, não mensagem local. */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}
